import React, { useState } from 'react';
import * as yup from 'yup';
import OForm from '../common/OForm';
import { CustomDialog, Button } from '@voicecare.ai/ui-library';
import { networkPlanTypes, placeOfServiceOptions } from '../../utils/constants/insuranceOptions';
import { 
  cleanPhoneNumber, 
  isValidUSPhoneNumber, 
  cleanTaxId, 
  isValidTaxId, 
  cleanNPI, 
  isValidNPI 
} from '../../utils/helpers/format-phone-number';
import InNetworkInsuranceField from './InNetworkInsuranceField';
import { useTheme } from '../../ThemeContext';
import { Controller } from 'react-hook-form';
import { Box, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
const validationSchema = yup.object().shape({
  facilityName: yup.string().required('Facility Name is required'),
  phoneNumber: yup
    .string()
    .required('Phone No. is required')
    .test('valid-phone', 'Enter a valid US phone number', (value) => {
      return isValidUSPhoneNumber(value);
    }),
  extension: yup
    .string()
    .required('Extension No. is required')
    .matches(/^\d{3,5}$/, 'Extension must be 3-5 digits')
    .min(3, 'Extension must be at least 3 digits')
    .max(5, 'Extension must be at most 5 digits'),
  faxNumber: yup
    .string()
    .required('Fax No. is required')
    .test('valid-fax', 'Enter a valid US fax number', (value) => {
      return isValidUSPhoneNumber(value);
    }),
    groupNPI: yup
    .string()
    .required('Group NPI is required')
    .test('valid-npi', 'Group NPI must be exactly 10 digits', (value) => {
      return isValidNPI(value);
    }),
  taxId: yup
    .string()
    .required('Tax ID is required')
    .test('valid-tax-id', 'Tax ID must be exactly 9 digits', (value) => {
      return isValidTaxId(value);
    }),
  networkPlanType: yup.array().of(yup.string()).min(1, 'Network Ins. Plan Type is required'),
  inNetworkIns: yup.array().of(yup.string()).min(1, 'In Network Ins. is required'),
  placeOfService: yup.string().required('Place of Service is required'),
  // outOfNetworkIns: yup.string().required('Out-of-Network Ins. is required'),
  // outOfNetworkPlanType: yup.string().required('Out of Network Ins. Plan Type is required'),
  // country: yup.string().required('Country is required'), // Removed - using US only
  state: yup.string().required('State is required'),
  city: yup.string().required('City is required'),
  zipCode: yup.string().required('Zip Code is required'),
  streetAddress: yup.string().required('Street Address is required'),
  departments: yup.array().of(
    yup.object().shape({
      name: yup.string()
        .nullable()
        .optional()
        .max(100, 'Department name must not exceed 100 characters')
        .trim(),
      faxNumber: yup
        .string()
        .nullable()
        .optional()
        .test('valid-fax', 'Enter a valid US fax number', (value) => {
          if (!value || value.trim() === '') return true; // Optional field
          return isValidUSPhoneNumber(value);
        })
    })
  ).optional()
});

const defaultInitialValues = {
  facilityName: '',
  phoneNumber: '',
  extension: '',
  faxNumber: '',
  groupNPI: '',
  taxId: '',
  networkPlanType: [],
  inNetworkIns: [],
  placeOfService: '',
  outOfNetworkIns: '',
  outOfNetworkPlanType: '',
  // country: '', // Removed - using US only
  state: '',
  city: '',
  zipCode: '',
  streetAddress: '',
  departments: [{ name: '', faxNumber: '' }]
};



const FacilityForm = ({ onSubmit, handleBack, setRenderType, renderType, initialValues, submitting }) => {
  const { themeMode, currentTheme } = useTheme();
  
  // State for confirmation dialog
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingFormValues, setPendingFormValues] = useState(null);

  // Check if this is a new facility creation (no initial values)
  const isNewFacility = !initialValues;

  // Custom onSubmit handler to show confirmation for new facilities
  const handleSubmit = (values) => {
    // Process departments - preserve IDs for existing departments
    let processedDepartments = values.departments || [];
    
    if (!isNewFacility && initialValues?.departments) {
      // When editing, preserve department IDs correctly
      // CRITICAL: Do NOT use array index - indices shift when departments are deleted!
      processedDepartments = values.departments.map((dept) => {
        // If department already has an ID (existing department), preserve it
        if (dept.id || dept._id) {
          return {
            ...dept,
            id: dept.id || dept._id
          };
        }
        
        // New department without ID - return as-is (backend will assign ID)
        return dept;
      });
      
      console.log('🔧 Processed departments with IDs:', processedDepartments.map(d => ({ name: d.name, id: d.id })));
    }
    
    const cleanedValues = {
      ...values,
      phoneNumber: cleanPhoneNumber(values.phoneNumber),
      faxNumber: cleanPhoneNumber(values.faxNumber),
      taxId: cleanTaxId(values.taxId),
      groupNPI: cleanNPI(values.groupNPI),
      country: 'US', // Hardcode country as US
      departments: processedDepartments
    };

    // For new facilities, show confirmation dialog
    if (isNewFacility) {
      setPendingFormValues(cleanedValues);
      setShowConfirmation(true);
    } else {
      // For existing facilities, submit directly
      onSubmit(cleanedValues);
    }
  };

  // Handle confirmation dialog confirm
  const handleConfirmSubmit = () => {
    if (pendingFormValues) {
      onSubmit(pendingFormValues);
      setShowConfirmation(false);
      setPendingFormValues(null);
    }
  };

  // Handle confirmation dialog cancel
  const handleCancelSubmit = () => {
    setShowConfirmation(false);
    setPendingFormValues(null);
  };

  // Get structured confirmation message
  const getConfirmationMessage = () => {
    const facilityName = pendingFormValues?.facilityName || 'this facility';
    const npiNumber = pendingFormValues?.groupNPI || 'the provided NPI';
    
    return (
      <div style={{ textAlign: 'left' }}>
        <p style={{ 
          margin: '0 0 20px 0', 
          fontSize: '16px', 
          lineHeight: '1.5',
          color: currentTheme?.homePage?.textColor || '#333'
        }}>
          You are about to create a new facility with the following details:
        </p>
        
        {/* Facility Info Box */}
        <div style={{
          backgroundColor: themeMode === 'dark' ? '#0b073a' : '#f8f9fa',
          border: `1px solid ${themeMode === 'dark' ? '#302d4c' : '#e9e8ea'}`,
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '20px',
          fontFamily: 'monospace'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            marginBottom: '12px',
            fontSize: '14px'
          }}>
            <span style={{ 
              fontWeight: '600',
              color: currentTheme?.homePage?.textColor || '#333'
            }}>
              Facility Name:
            </span>
            <span style={{ 
              color: themeMode === 'dark' ? '#a6a3bf' : '#666',
              fontWeight: '500'
            }}>
              {facilityName}
            </span>
          </div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            fontSize: '14px'
          }}>
            <span style={{ 
              fontWeight: '600',
              color: currentTheme?.homePage?.textColor || '#333'
            }}>
              Group NPI:
            </span>
            <span style={{ 
              color: '#e63e2b',
              fontWeight: '700',
              fontSize: '15px'
            }}>
              {npiNumber}
            </span>
          </div>
        </div>

        {/* Warning Box */}
        <div style={{
          backgroundColor: themeMode === 'dark' ? '#2d1b1b' : '#fff3cd',
          border: `1px solid ${themeMode === 'dark' ? '#5c3d3d' : '#ffeaa7'}`,
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px'
          }}>
            <span style={{
              fontSize: '20px',
              color: '#e63e2b',
              lineHeight: '1',
              marginTop: '2px'
            }}>
              ⚠️
            </span>
            <div>
              <p style={{
                margin: '0 0 8px 0',
                fontWeight: '700',
                color: '#e63e2b',
                fontSize: '15px'
              }}>
                IMPORTANT WARNING
              </p>
              <p style={{
                margin: '0',
                fontSize: '14px',
                lineHeight: '1.6',
                color: currentTheme?.homePage?.textColor || '#333'
              }}>
                The NPI (National Provider Identifier) <strong>cannot be changed</strong> once the facility is created. Please verify that the NPI number is correct before proceeding.
              </p>
            </div>
          </div>
        </div>

        <p style={{ 
          margin: '0', 
          fontSize: '15px',
          fontWeight: '600',
          color: currentTheme?.homePage?.textColor || '#333'
        }}>
          Do you want to proceed with creating this facility?
        </p>
      </div>
    );
  };

  const getFields = (watch, { setValue, getValues }) => {
    const departments = watch('departments') || [];
    
    const baseFields = [
      // Facility Details Section
      { fieldName: 'facilityName', renderType: 'text', label: 'Facility Name*', isRequired: true, section: 'Facility Details', placeholder: 'Facility Name' },
      { fieldName: 'phoneNumber', renderType: 'phoneNumber', label: 'Phone No.*', isRequired: true, section: 'Facility Details', placeholder: 'Phone No.' },
      { fieldName: 'extension', renderType: 'text', label: 'Extension No.*', isRequired: true, section: 'Facility Details', placeholder: 'Extension No.' },
      { fieldName: 'faxNumber', renderType: 'phoneNumber', label: 'Fax No.*', isRequired: true, section: 'Facility Details', placeholder: 'Fax No.' },
      { fieldName: 'groupNPI', renderType: 'text', label: 'Group NPI*', isRequired: true, section: 'Facility Details', disabled: initialValues ?? false, placeholder: 'Group NPI' },
      { fieldName: 'taxId', renderType: 'text', label: 'Group Tax ID*', isRequired: true, section: 'Facility Details', placeholder: 'Group Tax ID' },
      { 
        fieldName: 'inNetworkIns', 
        renderType: 'custom', 
        label: 'In Network Ins.*', 
        isRequired: true, 
        section: 'Facility Details',
        render: ({ hasError, errorMessage, control }) => (
          <Controller
            name="inNetworkIns"
            control={control}
            render={({ field }) => (
              <InNetworkInsuranceField
                value={field.value || []}
                onChange={field.onChange}
                themeMode={themeMode}
                hasError={hasError}
                errorMessage={errorMessage?.message}
              />
            )}
          />
        )
      },
      { 
        fieldName: 'networkPlanType', 
        renderType: 'select',
        type: "multi-select",  
        label: 'Network Ins. Plan Type*', 
        isRequired: true, 
        section: 'Facility Details', 
        options: networkPlanTypes,
        valueKey: 'value',
        labelKey: 'label',
        isCreatable: true,
        placeholder: 'Network Ins. Plan Type'
      },
      { 
        fieldName: 'placeOfService', 
        renderType: 'select',
        label: 'Place of Service*', 
        isRequired: true, 
        section: 'Facility Details', 
        options: placeOfServiceOptions,
        valueKey: 'value',
        labelKey: 'label',
        isCreatable: true,
        placeholder: 'Place of Service'
      }
      // { fieldName: 'outOfNetworkIns', renderType: 'select', label: 'Out-of-Network Ins.*', isRequired: true, section: 'Facility Details', options: insuranceOptions },
      // { fieldName: 'outOfNetworkPlanType', renderType: 'select', label: 'Out of Network Ins. Plan Type*', isRequired: true, section: 'Facility Details', options: networkPlanTypes },
    ];

    // Location Details Section
    const locationFields = [
      { fieldName: 'streetAddress', renderType: 'text', label: 'Street Address*', isRequired: true, section: 'Location Details', placeholder: 'Street Address' },
      { fieldName: 'city', renderType: 'text', label: 'City*', isRequired: true, section: 'Location Details', placeholder: 'City' },
      { fieldName: 'state', renderType: 'text', label: 'State*', isRequired: true, section: 'Location Details', placeholder: 'State' },
      { fieldName: 'zipCode', renderType: 'text', label: 'Zip Code*', isRequired: true, section: 'Location Details', placeholder: 'Zip Code' },
    ];

    // Department Fields - Push actions to the very end of container
    const departmentFields = departments.map((dept, index) => {
      
      return [{
        fieldName: `departments.${index}.name`,
        renderType: 'text',
        label: 'Department Name',
        isRequired: false,
        section: 'Departments',
        placeholder: 'Department Name'
      },
      {
        fieldName: `departments.${index}.faxNumber`,
        renderType: 'phoneNumber',
        label: 'Department Fax (Optional)',
        isRequired: false,
        section: 'Departments',
        placeholder: 'Department Fax'
      },
      // Empty spacer to push actions to the end
      {
        fieldName: `department_spacer_${index}`,
        renderType: 'custom',
        section: 'Departments',
        render: () => <Box sx={{ width: '100%' }} />
      },
      // Actions at the very end of the container
      {
        fieldName: `department_actions_${index}`,
        renderType: 'custom',
        section: 'Departments',
        render: () => {
          const hasValues = dept.name || dept.faxNumber;
          const canDelete = departments.length > 1;
          const isLastDepartment = index === departments.length - 1;
          
          return (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              height: '100%', 
              justifyContent: 'flex-end', 
              mt: { xs: 1, md: 0 }, 
              mr: 1.4
            }}>
              {/* Add Department button - only on last department */}
              {isLastDepartment && (
                <Button
                  size="small"
                  label="Add Department"
                  style={{ borderRadius: '6px' }}
                  onClick={() => {
                    const currentDepartments = getValues('departments') || [];
                    setValue('departments', [...currentDepartments, { name: '', faxNumber: '' }]);
                  }}
                  sx={{ color: '#ffffff', background: currentTheme.loginPage.loginButtonColor, mr: (canDelete || hasValues) ? 1 : 0 }}
                />
              )}
              {/* Show delete button for multiple departments OR clear button for single department with values */}
              {(canDelete || hasValues) && (
                <IconButton
                  size="small"
                  onClick={() => {
                    const currentDepartments = getValues('departments');
                    if (canDelete) {
                      // Simply remove the department from the array
                      setValue('departments', currentDepartments.filter((_, i) => i !== index));
                    } else {
                      // Clear the values but keep the department structure
                      currentDepartments[index] = { name: '', faxNumber: '' };
                      setValue('departments', [...currentDepartments]);
                    }
                  }}
                  sx={{ color: '#FF4D4F' }}
                  title={canDelete ? "Delete Department" : "Clear Department"}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          );
        }
      }];
    });

    return [
      ...baseFields,
      ...locationFields,
      ...departmentFields.flat()
    ];
  };

  return(
  <>
    <OForm
      formTitle={null}
      initialValues={initialValues || defaultInitialValues}
      fields={getFields}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      handleBack={handleBack}
      fieldsPerRow={4}
      submitButtonText={submitting ? "Submitting..." : "Submit"}
      headerButtonLabel="Back"
      headerButtonOnClick={() => setRenderType(renderType === 'list' ? 'form' : 'list')}
      submitting={submitting}
      scrollableSections={["Departments"]}
    />

    {/* Confirmation Dialog for New Facility Creation */}
    <CustomDialog
      open={showConfirmation}
      message={getConfirmationMessage()}
      onConfirm={handleConfirmSubmit}
      onCancel={handleCancelSubmit}
      themeMode={themeMode}
      currentTheme={currentTheme}
      loading={submitting}
    />
  </>
)};

export default FacilityForm; 
